import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {ClaimReportingPopup} from "../../../../../components/claims/ClaimReportingPopup";
import {
    setSessionStorage,
    getSessionStorage,
    deleteSessionStorage, scrollToTop
} from "../../../../../CommonFunctions";
import {BackButton} from "../../../../../components/navigation/BackButton";
import {NextButton} from "./NextButton";
import {IPolicyDetails} from "./CoverDetailsPage";
import {ProductsEnum, Policy, ICustomerPortalService, Types, FalconNavigatorHeader, SESSION_STORAGE_KEY_CLAIM_POLICY} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import * as moment from "moment";
import {FutureCoverBanner} from "../../renters/rentersClaims/FutureCoverBanner";
import { RadioInput } from "../../../../../components/inputs/RadioInput";
import {AllianzOnlineBadge} from "../../../../../components/AllianzOnlineBadge";

interface IWindscreenOrGlassState {
    windscreenOrGlass: IWindScreenOrGlass;
    policyDetails: IPolicyDetails;
}

export interface IWindScreenOrGlass {
    windscreenOrGlassDamaged: boolean | null;
    showWindscreenPopup: boolean;
}

@authorize
export class WindscreenOrGlassPagePlain extends React.Component<IWithRouterProps, IWindscreenOrGlassState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private static readonly WINDSCREEN_CLAIM_TITLE_TEXT: string = "Windscreen claim reporting";
    private static readonly WINDSCREEN_CLAIM_FIRST_PARAGRAPH: React.ReactNode = <p className="mt-4 mb-4">If you need to report only windscreen or glass damage call us on{' '}
        <b>our 24 hour helpline and select option #1</b></p>;

    constructor(props: IWithRouterProps) {
        super(props);

        const selectedPolicy = getSessionStorage(SESSION_STORAGE_KEY_CLAIM_POLICY);

        const windscreenOrGlass: IWindScreenOrGlass = getSessionStorage("windscreenOrGlass");
        let policyDetails: IPolicyDetails | null = getSessionStorage("policyDetails");

        if (selectedPolicy && selectedPolicy.policyNumber !== policyDetails?.selectedPolicy?.policyNumber){
            this.clearSession();
            policyDetails = null;
        }

        this.state = {
            windscreenOrGlass: windscreenOrGlass !== null ? windscreenOrGlass : {
                windscreenOrGlassDamaged: null,
                showWindscreenPopup: false
            } as IWindScreenOrGlass,
            policyDetails: policyDetails !== null ? policyDetails : {
                selectedPolicy,
                showEditContactDetailsPopup: false,
                party: null,
                partyLoaded: false
            } as IPolicyDetails
        };
        this.pluginTemplates.setPageTitle("Make a car claim");
    }

    public componentDidMount(): void {
        scrollToTop();
        const policyDetails: IPolicyDetails = {...this.state.policyDetails};
        if (!this.state.policyDetails.selectedPolicy){
            this.customerPortalService.getPolicies(ProductsEnum.MOTOR).subscribe(
                (policies: Policy[]) => {
                    if (policies && policies.length > 0){
                        policyDetails.selectedPolicy = policies[0];
                        this.setState({policyDetails});
                        setSessionStorage("policyDetails", policyDetails);
                    }
                }
            );
        } else {
            setSessionStorage("policyDetails", policyDetails);
        }
    }

    public render() {
        const {selectedPolicy} = this.state.policyDetails;

        if (selectedPolicy) {
            const today: string = moment().format("YYYY-MM-DD");
            const policyStartDate: string = moment(selectedPolicy.startDate).format("YYYY-MM-DD");
            const claimNotAllowed: boolean = moment(today).isBefore(policyStartDate);

            return (
                <React.Fragment>
                    <FalconHeader/>

                    <div className="page-contents">
                        <AllianzOnlineBadge/>
                        {this.state.windscreenOrGlass.showWindscreenPopup &&
                            <ClaimReportingPopup title={WindscreenOrGlassPagePlain.WINDSCREEN_CLAIM_TITLE_TEXT}
                                 bodyTopParagraph={WindscreenOrGlassPagePlain.WINDSCREEN_CLAIM_FIRST_PARAGRAPH}
                                 showModal={this.state.windscreenOrGlass.showWindscreenPopup}
                                 onModalHide={this.toggleModal}
                                 id={"windscreenReporting"}
                                 localPhoneNumber="08000 304 897"
                                 abroadPhoneNumber="0120 284 8509"
                            />
                        }
                        <FalconNavigatorHeader back={{ action: this.clearSession }} title="Make a car claim" />
                        {claimNotAllowed && <FutureCoverBanner policyStartDate={moment(policyStartDate).format("DD/MM/YYYY")}/>}
                        {!claimNotAllowed && this.renderClaimReasonBanner()}
                        {this.renderWindscreenOrGlassDamageSection(claimNotAllowed)}
                        {this.state.windscreenOrGlass.windscreenOrGlassDamaged === false &&
                            <NextButton disabled={claimNotAllowed}
                                buttonId={"submitWindscreenOrGlassPage"}
                                onClick={this.handleRedirect}/>
                        }
                        <BackButton isTop={false}
                            onButtonPress={this.clearSession}/>
                    </div>

                    <FalconFooter/>
                </React.Fragment>
            )
        } else {
            return (<Overlay show={true}/>)
        }

    }

    private renderClaimReasonBanner = (): React.ReactNode => {
        return (
            <React.Fragment>
                <div className="banner px-4">
                    <div className="text-blue-2">
                        Start your claim online and tell us what happened. <b>It should take about a minute.</b>
                    </div>
                </div>
            </React.Fragment>
        )
    };

    private renderWindscreenOrGlassDamageSection = (claimNotAllowed: boolean): React.ReactNode => {
        const infoPanel: React.ReactNode = (
            <div className="info-button-panel font-weight-normal">
                (e.g. If you need to claim for repair or replacement of cracked, chipped or broken glass on your car)
            </div>
        );

        return (
            <div className="panel">
                <RadioInput label={<label>Does your claim relate to damage for windscreen or glass only?</label>}
                    id="windscreenOrGlassDamaged"
                    onChange={this.setWindscreenOrGlassDamaged}
                    value={this.state.windscreenOrGlass.windscreenOrGlassDamaged!}
                    infoSection={infoPanel}
                    inlineDisplay={true}
                    disabled={claimNotAllowed}
                />
            </div>
        )
    };

    private handleRedirect = (): void => {
        this.props.navigate("/dashboard/windscreen/urgent");
    };

    private setWindscreenOrGlassDamaged = (event: any): void => {
        const state: boolean = event.target.value === "true";
        const windscreenOrGlass: IWindScreenOrGlass = {...this.state.windscreenOrGlass};
        if (state)  {
            windscreenOrGlass.showWindscreenPopup = state;
        }
        windscreenOrGlass.windscreenOrGlassDamaged = state;
        this.setState({windscreenOrGlass});
        setSessionStorage("windscreenOrGlass", windscreenOrGlass);
    };

    private toggleModal = (): void => {
        const windscreenOrGlass: IWindScreenOrGlass = {...this.state.windscreenOrGlass};
        windscreenOrGlass.showWindscreenPopup = !windscreenOrGlass.showWindscreenPopup;
        this.setState({windscreenOrGlass});
        setSessionStorage("windscreenOrGlass", windscreenOrGlass);
    };

    private clearSession = (): void => {
        deleteSessionStorage("onlineMotorClaimRequest");
        deleteSessionStorage("urgentClaim");
        deleteSessionStorage("whenDidItHappen");
        deleteSessionStorage("windscreenOrGlass");
        deleteSessionStorage("policyDetails");
        deleteSessionStorage("contactDetails");
    };
}

export const WindscreenOrGlassPage = withRouter(WindscreenOrGlassPagePlain)
